import { sendPatchRequest } from '../../utils';
import RowContextMenuButtonColumn from '../formatters/RowContextMenuButtonColumn';
import { addAlert } from '../../alerts';
import { initializeTable } from '../tables';
import { postPrompt } from '../../prompts.js';

function initializeUsersTable() {
    const rootElement = document.querySelector('#usersTable');
    const groupsData = JSON.parse(rootElement.dataset.groups);
    if (!rootElement) return;

    const API_URL = '/api/users/';
    const saveButton = document.getElementById('save-user-changes-button');

    const groupNameMap = new Map(
        groupsData.map((group) => [group.id, group.name]),
    );

    const setButtonsEnabled = (isEnabled) => {
        saveButton.disabled = !isEnabled;
    };

    const textWrapperFormatter = (cell) => {
        const value = cell.getValue();
        return `<span class="cell-text-wrapper">${value || ''}</span>`;
    };

    const groupIdsMutator = (value) => {
        if (!Array.isArray(value)) return [];
        return value.map((group) =>
            group !== null && typeof group === 'object' ? group.id : group,
        );
    };

    const groupsFormatter = (cell) => {
        const value = cell.getValue();
        if (!Array.isArray(value) || value.length === 0) return '';

        return value
            .map((id) => groupNameMap.get(id) || '')
            .filter(Boolean)
            .join(', ');
    };

    const hasSameGroups = (currentValue, previousValue) => {
        const current = Array.isArray(currentValue) ? currentValue : [];
        const previous = Array.isArray(previousValue) ? previousValue : [];

        return (
            current.length === previous.length &&
            current.every((id) => previous.includes(id))
        );
    };

    const checkTableState = () => {
        const editedCells = table.getEditedCells();
        setButtonsEnabled(editedCells.length > 0);
    };

    const groupEditorValues = groupsData.map((group) => ({
        label: group.name,
        value: group.id,
    }));

    const userRowContextMenu = (e, row) => {
        const menu = [
            {
                label: gettext('Generuj klucz API'),
                action: (e, clickedRow) => {
                    const rowData = clickedRow.getData();

                    postPrompt({
                        options: {
                            title: gettext('Potwierdzenie'),
                            html: gettext(
                                'Czy na pewno chcesz wygenerować nowy klucz API dla tego użytkownika? Spowoduje to unieważnienie poprzedniego klucza.',
                            ),
                        },
                        url: `${API_URL}${rowData.id}/create-api-key/`,
                        successAction: async (response) => {
                            const data = await response.json();
                            addAlert('success', data.message);
                        },
                        failedAction: async (response) => {
                            let message = gettext(
                                'Wystąpił błąd podczas generowania klucza API.',
                            );
                            if (response) {
                                try {
                                    const data = await response.json();
                                    if (data && data.message) {
                                        message = data.message;
                                    }
                                } catch (e) {}
                            }
                            addAlert('error', message);
                        },
                    });
                },
            },
        ];
        return menu;
    };

    const columns = [
        { title: 'ID', field: 'id', visible: false },
        { title: '#', formatter: 'rownum', width: 60, headerSort: false },
        {
            title: 'Imię',
            field: 'first_name',
            formatter: textWrapperFormatter,
            headerSort: true,
        },
        {
            title: 'Nazwisko',
            field: 'last_name',
            formatter: textWrapperFormatter,
            headerSort: true,
        },
        {
            title: 'Email',
            field: 'email',
            formatter: textWrapperFormatter,
            headerSort: true,
        },
        {
            title: 'Grupy',
            field: 'groups',
            mutator: groupIdsMutator,
            formatter: groupsFormatter,
            editor: 'list',
            headerSort: false,
            cssClass: 'groups-cell-editable',
            editorParams: {
                values: groupEditorValues,
                multiselect: true,
            },
        },
        {
            title: gettext('Akcje'),
            formatter: RowContextMenuButtonColumn,
            width: 100,
            resizable: false,
            headerSort: false,
            hozAlign: 'center',
        },
    ];

    const table = initializeTable({
        tableElement: rootElement,
        filtersElement: document.querySelector('#usersFilters .form-fields'),
        ajaxURL: API_URL,
        columns: columns,
        rowContextMenu: userRowContextMenu,
        pillsConfig: {
            containerSelector: '.active-filters-container',
        },
        height: '500px',
        layout: 'fitColumns',
    });

    table.on('cellEdited', (cell) => {
        if (hasSameGroups(cell.getValue(), cell.getOldValue())) {
            cell.clearEdited();
        }
        checkTableState();
    });

    saveButton.addEventListener('click', async () => {
        const editedCells = table.getEditedCells();
        if (editedCells.length === 0) return;

        const promises = [];
        const updates = new Map();

        editedCells.forEach((cell) => {
            const rowData = cell.getRow().getData();
            updates.set(rowData.id, { groups_ids: rowData.groups });
        });

        updates.forEach((data, id) => {
            promises.push(sendPatchRequest(`${API_URL}${id}/`, data));
        });

        try {
            await Promise.all(promises);
            addAlert('success', 'Zmiany pomyślnie zapisane.');
            editedCells.forEach((cell) => cell.clearEdited());
            checkTableState();
            table.replaceData();
        } catch (error) {
            addAlert('error', 'Wystąpił błąd podczas zapisywania zmian.');
        }
    });

    window.tables.initializeTableFilter();
    setButtonsEnabled(false);
}

export default initializeUsersTable;
